import { RelativeComponentLoader } from "@/components/loader";
import { Paginate } from "@/components/paginate";
import { DataTable } from "@/components/tables";
import { getExportLineage } from "@/helpers/apis";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NoResource } from "@/components/noResource";
import { GetExportLineageItem } from "../exportLineage/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { Typography } from "@altimateai/lego";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { TableRowItem } from "@/components/tables/types";

const PAGE_SIZE = 10;

export const ExportLineageList = () => {
  useBreadcrumb("Export Lineage");
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Sync page and page size from URL on mount
  useEffect(() => {
    const urlPage = searchParams.get("page");
    const urlPageSize = searchParams.get("size");

    if (urlPage) {
      const pageNum = parseInt(urlPage) - 1; // Convert from 1-based to 0-based
      if (pageNum >= 0 && pageNum !== page) {
        setPage(pageNum);
      }
    }

    if (urlPageSize) {
      const size = parseInt(urlPageSize);
      if (size > 0 && size !== pageSize) {
        setPageSize(size);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["GET-EXPORT-LINEAGE", page, pageSize],
    queryFn: () => getExportLineage({ page: page + 1, size: pageSize }),
  });

  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  if (isError) {
    return (
      <NoResource
        title="Failed to load lineages"
        message="An error occurred while loading export lineages. Please try again later."
      />
    );
  }

  const handlePageChange = (newPage: number) => {
    updateSearchParamsByKey({
      page: `${newPage + 1}`, // Convert to 1-based for URL
    });
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    updateSearchParamsByKey({
      size: `${newSize}`,
      page: "1", // Reset to first page when changing page size
    });
    setPageSize(newSize);
    setPage(0); // Reset to first page (0-based)
  };

  if (!data?.total) {
    return (
      <NoResource
        title="No lineage exported"
        message="Please visit the extension to export lineage."
      />
    );
  }
  return (
    <div>
      <Typography variant="page-heading" className="al-mb-2">
        Exported lineages
      </Typography>
      <DataTable<GetExportLineageItem>
        header={[
          {
            id: 1,
            label: "Name",
            uid: "name",
            render: item => <div>{item.name}</div>,
          },
          {
            id: 2,
            label: "User",
            uid: "user",
            render: item => (
              <div>
                {item.user?.first_name} {item.user?.last_name}
              </div>
            ),
          },
          {
            id: 3,
            label: "Created at",
            uid: "created_at",
            render: item => <div>{dayjs(item.created_at).format("DD MMM YYYY hh:mm")}</div>,
          },
        ]}
        data={{ items: data?.items }}
        getRowUrl={(item: GetExportLineageItem) => `/collaboration/export-lineage/${item.id}`}
        getRowState={(item: TableRowItem) => ({ entityName: (item as GetExportLineageItem).name })}
        idKey="id"
        minWidth={700}
      />
      {data && (
        <Paginate
          showPageSize
          itemCount={data.total}
          page={page}
          pageSize={pageSize}
          numPages={data.pages}
          onPageClick={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
};
